"use client";

import { ProductDemoForm } from "@/components/product-demo-form";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  type ComponentProps,
  type MouseEvent,
  useEffect,
  useState,
} from "react";

const OPEN_DEMO_EVENT = "reecost:open-demo";

function getProductName(pathname: string) {
  if (pathname.includes("/retail")) {
    return "Retail";
  }

  if (pathname.includes("/restaurant") || pathname.includes("/fine-dine")) {
    return "Restaurant";
  }

  return "Retail & Restaurant";
}

// Keeps the /p-form href as a fallback for modified clicks and no-JS.
export function DemoLink({
  onClick,
  ...props
}: Omit<ComponentProps<typeof Link>, "href">) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);

    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    window.dispatchEvent(new Event(OPEN_DEMO_EVENT));
  };

  return <Link {...props} href="/p-form" onClick={handleClick} />;
}

export function DemoModal() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_DEMO_EVENT, open);
    return () => window.removeEventListener(OPEN_DEMO_EVENT, open);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const productName = getProductName(pathname ?? "");

  return (
    <dialog
      aria-label="Book your free demo"
      className="fixed inset-0 z-[1000] m-0 flex h-full max-h-none w-full max-w-none overflow-y-auto border-0 bg-[#12033a]/70 p-4 backdrop-blur-sm"
      open
    >
      <button
        aria-label="Close demo form"
        className="absolute inset-0 cursor-default"
        onClick={() => setIsOpen(false)}
        tabIndex={-1}
        type="button"
      />
      <div className="relative z-10 m-auto w-full min-w-0 max-w-[480px]">
        <ProductDemoForm
          onClose={() => setIsOpen(false)}
          productName={productName}
        />
      </div>
    </dialog>
  );
}
